import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, MessageCircle, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const pollInterval = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    
    // Poll every 10 seconds
    pollInterval.current = setInterval(() => {
      fetchUnreadCount();
    }, 10000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleNotificationClick = async (notification) => {
    console.log('[NotificationBell] Notification clicked:', notification);
    console.log('[NotificationBell] Notification ride_id:', notification.ride_id);
    console.log('[NotificationBell] Notification type:', notification.type);
    
    // Mark as read
    try {
      console.log('[NotificationBell] Marking notification as read:', notification.id);
      await axios.post(`/notifications/${notification.id}/read`);
      console.log('[NotificationBell] Mark as read SUCCESS');
      fetchUnreadCount();
      
      // Navigate to ride if applicable
      if (notification.ride_id) {
        const targetUrl = notification.type === 'ride_complete' 
          ? `/ride/${notification.ride_id}?showRating=true`
          : `/ride/${notification.ride_id}`;
        console.log('[NotificationBell] Navigating to:', targetUrl);
        navigate(targetUrl);
      } else {
        console.warn('[NotificationBell] Notification has no ride_id:', notification);
        toast.error('Unable to navigate - no ride associated with this notification');
      }
      
      setIsOpen(false);
    } catch (error) {
      // VERBOSE PRODUCTION ERROR LOGGING - DO NOT REMOVE
      console.error('============================================');
      console.error('[NotificationBell] CRITICAL ERROR - handleNotificationClick FAILED');
      console.error('[NotificationBell] Notification ID:', notification.id);
      console.error('[NotificationBell] Error name:', error.name);
      console.error('[NotificationBell] Error message:', error.message);
      console.error('[NotificationBell] Error code:', error.code);
      console.error('[NotificationBell] Response status:', error.response?.status);
      console.error('[NotificationBell] Response data:', JSON.stringify(error.response?.data));
      console.error('[NotificationBell] Full error object:', error);
      console.error('============================================');
      toast.error('Failed to process notification');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post('/notifications/mark-all-read');
      fetchUnreadCount();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_message':
        return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'ride_join':
        return <Users className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-purple-500" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-2">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read"
            >
              <Check className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`p-3 cursor-pointer ${
                  !notification.read ? 'bg-accent' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid="notification-item"
              >
                <div className="flex gap-3 w-full">
                  <div className="mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{notification.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
